import { useCallback, useEffect, useState } from 'react';
import Reveal from '@/components/base/Reveal';

interface LegendItem {
  icon: string;
  title: string;
  detail: string;
}

// Generic descriptions of what the exported report contains. Deliberately free
// of specific figures so the frame never competes with — or contradicts — the
// numbers the rest of the journal already shows.
const legendItems: LegendItem[] = [
  {
    icon: 'ri-line-chart-line',
    title: 'Balance curve',
    detail: 'Every closed trade plotted end to end, entry by entry.',
  },
  {
    icon: 'ri-wallet-3-line',
    title: 'Account summary',
    detail: 'Balance, equity and margin standing at the close of the run.',
  },
  {
    icon: 'ri-bar-chart-2-line',
    title: 'Performance breakdown',
    detail: 'Win split by side, trade extremes and streak statistics.',
  },
  {
    icon: 'ri-shield-check-line',
    title: 'Drawdown',
    detail: 'Peak-to-trough balance drawdown recorded across the test.',
  },
];

function TesterReport() {
  const [open, setOpen] = useState(false);
  // `closing` plays the exit animation while the overlay is still mounted, so
  // the lightbox eases out instead of disappearing in a single frame.
  const [closing, setClosing] = useState(false);

  const requestClose = useCallback(() => {
    setClosing((prev) => (prev ? prev : true));
  }, []);

  // Once the exit animation has finished, actually unmount the overlay.
  useEffect(() => {
    if (!closing) return undefined;
    const timer = window.setTimeout(() => {
      setOpen(false);
      setClosing(false);
    }, 190);
    return () => window.clearTimeout(timer);
  }, [closing]);

  // Lock the page behind the lightbox and let Escape close it, so the enlarged
  // report behaves like a proper overlay instead of just a floating picture.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') requestClose();
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, requestClose]);

  return (
    <section className="w-full px-4 md:px-8 lg:px-12 py-16 md:py-24">
      <div className="max-w-[1440px] mx-auto">
        <Reveal>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-5">
            <div>
              <p className="text-[11px] font-data tracking-[0.24em] text-primary-600 mb-5">
                STRATEGY TESTER REPORT
              </p>
              <h2 className="font-heading font-extrabold tracking-tight text-foreground-950 text-[30px] leading-[1.08] sm:text-[40px] lg:text-[46px] max-w-[760px]">
                The Same Report, Unedited.
              </h2>
              <p className="mt-4 text-[14px] md:text-[15px] leading-relaxed text-foreground-600 max-w-[620px]">
                Below is the raw export from the MetaTrader 5 Strategy Tester. Nothing is
                re-drawn or summarised — the curve, the account summary and the full performance
                breakdown are shown exactly as the platform produced them.
              </p>
            </div>
            <span className="inline-flex items-center gap-2 h-8 px-3.5 rounded-full border border-accent-300 bg-accent-100 text-[11px] font-data tracking-[0.16em] text-accent-900 whitespace-nowrap lg:mb-2">
              <i className="ri-verified-badge-line text-[14px]"></i>
              UNEDITED EXPORT
            </span>
          </div>
        </Reveal>

        <Reveal delay={100}>
          <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-start">
            {/* report viewer */}
            <figure className="lg:col-span-8 w-full rounded-lg border border-background-300 bg-background-100 overflow-hidden">
              <figcaption className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 px-4 md:px-5 py-2.5 border-b border-background-200 bg-background-100">
                <span className="inline-flex items-center gap-2.5">
                  <span className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full bg-accent-400"></span>
                    <span className="w-2.5 h-2.5 rounded-full bg-secondary-300"></span>
                    <span className="w-2.5 h-2.5 rounded-full bg-background-300"></span>
                  </span>
                  <span className="text-[11px] font-data tracking-[0.16em] text-foreground-500 whitespace-nowrap">
                    MT5 · STRATEGY TESTER
                  </span>
                </span>
                <span className="inline-flex items-center gap-1.5 text-[11px] font-data tracking-wide text-foreground-400 whitespace-nowrap">
                  <i className="ri-file-chart-line text-[13px]"></i>
                  StrategyTester-XAUUSD
                </span>
              </figcaption>
              <div className="p-3 md:p-5 bg-background-50">
                <button
                  type="button"
                  onClick={() => setOpen(true)}
                  aria-label="Enlarge the Strategy Tester report"
                  className="group relative block w-full rounded-md cursor-zoom-in"
                >
                  {/* @replace:tester-report */}
                  <img
                    src="/images/tester-report.png"
                    alt="MetaTrader 5 Strategy Tester report for the XAUUSD MT5 Expert Advisor"
                    title="XAUUSD MT5 Expert Advisor — Strategy Tester report"
                    className="w-full h-auto rounded-md border border-background-200 bg-background-50 object-top transition-opacity duration-200 group-hover:opacity-95"
                  />
                  <span className="pointer-events-none absolute bottom-2.5 right-2.5 inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full bg-foreground-950/75 text-background-50 text-[11px] font-data tracking-wide opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap">
                    <i className="ri-zoom-in-line text-[13px]"></i>
                    Enlarge
                  </span>
                </button>
              </div>
            </figure>

            {/* explainer */}
            <div className="lg:col-span-4 w-full flex flex-col gap-4">
              <div className="rounded-lg border border-background-300 bg-background-100 p-5 md:p-6">
                <p className="text-[11px] font-data tracking-[0.22em] text-foreground-500">
                  WHAT&apos;S IN IT
                </p>
                <ul className="mt-5 flex flex-col gap-4">
                  {legendItems.map((item) => (
                    <li key={item.title} className="flex items-start gap-3">
                      <span className="w-9 h-9 shrink-0 flex items-center justify-center rounded-md border border-background-200 bg-background-50 text-primary-600">
                        <i className={`${item.icon} text-[16px]`}></i>
                      </span>
                      <span className="min-w-0">
                        <span className="block text-[14px] font-medium text-foreground-950">
                          {item.title}
                        </span>
                        <span className="block mt-0.5 text-[12.5px] leading-relaxed text-foreground-500">
                          {item.detail}
                        </span>
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              <button
                type="button"
                onClick={() => setOpen(true)}
                className="w-full inline-flex items-center justify-center gap-2 h-11 rounded-lg border border-background-300 bg-background-50 text-[13px] font-semibold text-foreground-950 whitespace-nowrap transition-colors cursor-pointer hover:border-primary-400 hover:text-primary-700"
              >
                <i className="ri-zoom-in-line text-[15px]"></i>
                Open report full size
              </button>

              <div className="rounded-lg border border-background-300 bg-background-50 px-5 py-4">
                <p className="text-[12.5px] leading-relaxed text-foreground-500">
                  The report is generated by the platform, not by this site. Enlarge it to read the
                  detail line by line.
                </p>
              </div>
            </div>
          </div>
        </Reveal>
      </div>

      {open && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Strategy Tester report, enlarged"
          onClick={requestClose}
          className={`${closing ? 'lightbox-fade-out' : 'lightbox-fade'} fixed inset-0 z-50 flex items-center justify-center p-4 md:p-8 bg-foreground-950/85 backdrop-blur-sm`}
        >
          <button
            type="button"
            onClick={requestClose}
            aria-label="Close enlarged report"
            className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center rounded-full border border-background-50/30 text-background-50 transition-colors cursor-pointer hover:bg-background-50/10"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
          {/* @replace:tester-report */}
          <img
            src="/images/tester-report.png"
            alt="MetaTrader 5 Strategy Tester report for the XAUUSD MT5 Expert Advisor, enlarged"
            title="XAUUSD MT5 Expert Advisor — Strategy Tester report"
            onClick={(event) => event.stopPropagation()}
            className={`${closing ? 'lightbox-pop-out' : 'lightbox-pop'} max-w-full max-h-full rounded-md border border-background-50/20 object-contain`}
          />
        </div>
      )}
    </section>
  );
}

export default TesterReport;